import React, { useEffect } from 'react';
import css from '@src/styles/widget.scss';
import { createRoot } from 'react-dom/client';
import Offer from '@src/js/Components/Aggregation/Offer';
import OfficeTable from '@src/js/Components/Aggregation/OfficeTable';
import Reaction from '@src/js/Components/Base/Reaction';
import style from '@src/js/Style';
import manager from './TranslationManager';
import List from './Components/Blog/List';
import History from './Components/Calculator/History';
import CalculatorWidget from './Widget/CalculatorWidget';
import TemplateWrapper from './Components/Base/TemplateWrapper';
import MortgageCompareComponent from './Components/Calculator/MortgageCompareComponent';
import RatesComponent from './Components/Calculator/RatesComponent';
import Timeline from './Components/Base/Timeline';
import Card from './Components/Aggregation/Card';
import OfferLink from './Components/Aggregation/OfferLink';
import TopOffers from './Components/Aggregation/TopOffers';
import MonthlyOffers from './Components/Aggregation/MonthlyOffers';
import StickyOfferLink from './Components/Aggregation/StickyOfferLink';
import TimelineSingle from './Components/Base/TimelineSingle';
import Menu from './Components/Site/Menu';

export default class FinansoWidget {

    static uniqueId = 0;

    static get mapping() {

        return {
            menu: (props) => <Menu {...props} />,
            mortgageCalculator: (props) => <CalculatorWidget type='mortgage' {...props} />,
            reaction: (props, dataset) => <Reaction type={ dataset.type } />,
            officeTable: (props) => <OfficeTable {...props} />,
            blogList: (props) => <List {...props} />,
            'calculator-history': (props) => <History {...props} />,
            mortgageCompare: () => <MortgageCompareComponent i18nLink={ window.location.href } />,
            rates: () => <RatesComponent i18nLink={ window.location.href } />,
            timeline: (props, dataset) => <Timeline context={{
                prefix: dataset.id,
                storages: props.storage,
            }} />,
            timelineSingle: (props, dataset) => <TimelineSingle context={{
                prefix: dataset.id,
                storages: props.storage,
            }} />,
            productCard: (props) => <Card {...props} context={{ storages: props.storage }} />,
            offerLink: (props) => <OfferLink {...props} context={{ storages: props.storage }} />,
            topOffersByCompany: (props, dataset) => <TopOffers {...props} {...dataset} />,
            monthlyOffers: (props, dataset) => <MonthlyOffers {...props} {...dataset} />,
            stickyOfferLink: (props, dataset) => <StickyOfferLink {...props} {...dataset} />
        };
    }

    static generateUniqueId() {

        return ++FinansoWidget.uniqueId;
    }

    handleWidget(el) {

        const
            type = el.dataset.widget,
            method = FinansoWidget.mapping[type] ?? undefined;

        if (method == undefined)
            return console.error(`Widget '${type}' not found`);

        const options = {};
        el
            .querySelectorAll('[data-widget-prop]')
            .forEach((propElement) => {

                const value = propElement.tagName.toUpperCase() == 'INPUT'
                    ? propElement.value
                    : propElement.innerHTML;

                if (options[propElement.dataset.widgetProp] != undefined)

                    if (Array.isArray(options[propElement.dataset.widgetProp]))

                        options[propElement.dataset.widgetProp].push(value);

                    else
                        options[propElement.dataset.widgetProp] = [
                            options[propElement.dataset.widgetProp],
                            value,
                        ];

                else

                    options[propElement.dataset.widgetProp] = value;


                if (propElement.dataset?.widgetType == 'json')
                    options[propElement.dataset.widgetProp] = JSON.parse(options[propElement.dataset.widgetProp]);
            });

        const root = this.inject(el, { appendClass: false });
        root.render(method(options, el.dataset));

        return true;
    }

    handle(container) {

        container
            .querySelectorAll('[data-widget]')
            .forEach((el) => this.handleWidget(el));
    }

    inject(container, options = { appendClass: true }) {

        if (container.root == undefined) {

            if (options.appendClass && !container.classList.contains(css.widget))
                container.classList.add(css.widget);

            // eslint-disable-next-line no-param-reassign
            container.root = createRoot(container);
        }

        return container.root;
    }

    setLocale(locale) {

        manager.locale = locale;
    }

    showOffers(container, items) {

        const
            root = this.inject(container),

            primaryPromoHtml = document.body
                .querySelector('[data-role="search-primary-promo"]')
                ?.content
                ?.firstElementChild
                ?.outerHTML,
            primaryPromoPosition = 0,

            promoHtml = document.body
                .querySelector('[data-role="search-promo"]')
                ?.content
                ?.firstElementChild
                ?.outerHTML,
            promoPosition = 1,

            offers = [];

        items.forEach((offer, index) => {

            /** offer block */
            offers.push(<Offer

                key={ offer.id }
                isBest={ offer.isBest }

                anchor={ offer.anchor }
                companyAnchor={ offer.company.anchor }
                companyLink={ offer.company.link }
                imageUri={ offer.imageUri }

                rating={ offer.rating.value }
                ratingCount={ offer.rating.count }

                scoreValue={ offer.score.value }

                sum={ offer.details.sum }
                rate={ offer.details.rate }
                term={ offer.details.term }

                link={ offer.link }

            />);

            if (offer.schemaOrg != null)
                offers.push(<script key={ `schema${offer.id}` } type="application/ld+json">
                    { JSON.stringify(offer.schemaOrg) }
                </script>);

            /** primary promo block */
            if (index == primaryPromoPosition && primaryPromoHtml) {

                const wrapper = <TemplateWrapper
                    className={ style('w-full') }
                    key={ 'primaryPromoBlock' }
                    selector='[data-role="search-primary-promo"]'
                />;
                offers.push(wrapper);
            }

            /** secondary promo block */
            if (index == promoPosition && promoHtml)
                offers.push(
                    <div
                        key={ 'secondaryPromoBlock' }
                        className={ style('w-full') }
                        dangerouslySetInnerHTML={{ __html: promoHtml }} />,
                );

        });

        const Application = () => {

            const ref = React.createRef();

            useEffect(() => {

                document.dispatchEvent(new CustomEvent('DOMContentUpdated', {
                    detail: {
                        target: ref.current,
                    },
                }));
            });

            return <React.StrictMode ref={ ref }>{ offers }</React.StrictMode>;
        };


        root.render(<Application />);
    }

}